import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaPhone, FaMapMarkerAlt, FaEnvelope } from "react-icons/fa";
import { motion } from "framer-motion"
import Contactbg from "../assets/Contactbg.png";

const Contact = () => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "+91",
    phone: "",
    companyName: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  const handlePhoneChange = (e) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
    setForm({ ...form, phone: onlyNums });
  };

  const validate = () => {
    const newErrors = {};

    if (!form.firstName.trim()) newErrors.firstName = "First name is required";
    if (!form.lastName.trim()) newErrors.lastName = "Last name is required";

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(form.phone)) {
      newErrors.phone = "Phone must be exactly 10 digits";
    }

    if (!form.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }
    if (!form.subject.trim()) newErrors.subject = "Subject is required";
    if (!form.message.trim()) newErrors.message = "Message is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const response = await fetch("https://api.microwellindustries.com/api/enquiries", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitted(true);
        setSuccess(true);

        setForm({
          firstName: "",
          lastName: "",
          email: "",
          countryCode: "+91",
          phone: "",
          companyName: "",
          subject: "",
          message: "",
        });

        setTimeout(() => {
          setSuccess(false);
          setSubmitted(false);
        }, 4000);
      } else {
        alert(data.message || "Failed to send message.");
      }
    } catch (error) {
      console.error("Error sending contact form:", error);
      alert("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden w-full bg-gradient-to-br from-gray-100 to-gray-200 p-6 flex flex-col items-center">
      {/* Header Section with background image */}
      <header
        className="relative h-64 md:h-72 bg-cover bg-center text-white flex flex-col items-center justify-center text-center py-20 px-16 mb-5 -mt-6 w-screen"
        style={{ backgroundImage: `url(${Contactbg})` }}
      >
        <div className="absolute inset-0 bg-blue-900 bg-opacity-70"></div>
        <motion.div
          className="relative z-10 max-w-3xl"
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="max-w-2xl mx-auto text-base md:text-lg">
            We're here to help you! Reach out for any queries, support, or collaboration.
          </p>
        </motion.div>
      </header>

      <div
        className="w-full max-w-7xl flex flex-col md:flex-row gap-4 items-start justify-center"
        data-aos="fade-up"
      >
        {/* Left Info Section */}
        <div
          className="w-full md:w-1/2 bg-white/60 backdrop-blur-md p-8 rounded-xl shadow-lg space-y-4"
          data-aos="fade-right"
        >
          <h2 className="text-3xl font-bold text-gray-700">
            Get In <span className="text-blue-700">Touch</span>
          </h2>

          <p className="text-gray-600">We're here to help you…</p>

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-gray-700">
              <FaMapMarkerAlt className="text-blue-600 flex-shrink-0" />
              <span>
                <strong>Head Office:</strong> Miltat No. 1309, A-15, Naz Kantan, Khali Char Rasta,
                Sidhpur, Patan, Gujarat - 384151
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <FaPhone className="text-green-600" flex-shrink-0 />
              <a href= "tel:+918347117507" className="hover:text-blue-500">
                <span>+91 8347117507</span>
              </a>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <FaMapMarkerAlt className="text-blue-600 flex-shrink-0" />
              <span>
                <strong>Branch Office:</strong> Shop No. 16, Kharod Plaza, Opp. Hotel Landmark,
                Village - Kharod, Ankleshwar, Dist - Bharuch, (N-Gujarat) - 394115
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <FaPhone className="text-green-600" flex-shrink-0 />
              <a href= "tel:+91 9512891584" className="hover:text-blue-500">
                <span>+91 9512891584</span>
              </a>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <FaEnvelope className="text-red-600" flex-shrink-0 />
              <span>info.microwellindustries@gmail.com</span>
            </div>
          </div>

          <iframe
            title="MICRO-WELL INDUSTRIES LOCATION"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3467.512657669121!2d72.36534607511972!3d23.889146478576023!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395cf5d26bab9f2d%3A0x4e5fe7c737ce0583!2sMICRO-WELL%20INDUSTRIES!5e1!3m2!1sen!2sin!4v1756285351312!5m2!1sen!2sin"
            className="w-full h-44 rounded-md border border-gray-300"
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </div>

        {/* Right Form Section */}
        <form
          onSubmit={handleSubmit}
          className="w-full md:w-1/2 bg-white/70 backdrop-blur-md rounded-xl shadow-xl p-8 space-y-6"
          data-aos="fade-left"
        >
          <div className="text-center mb-6">
            <h2 className="text-xl md:text-2xl font-medium text-gray-700">
              Have any questions?
            </h2>
            <h2 className="text-x md:text-xl font-medium text-blue-700">
              Send us a message
            </h2>
          </div>

          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded text-center animate-pulse">
              Thanks for contacting us! We will be in touch with you shortly.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium">
                First Name<span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="First Name"
              />
              {errors.firstName && (
                <p className="text-red-600 text-sm mt-1">{errors.firstName}</p>
              )}
            </div>
            <div>
              <label className="block text-gray-700 font-medium">
                Last Name<span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Last Name"
              />
              {errors.lastName && (
                <p className="text-red-600 text-sm mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-medium">
              Email<span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="your@email.com"
            />
            {errors.email && (
              <p className="text-red-600 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-1">
              Phone<span className="text-red-600">*</span>
            </label>
            <div className="flex gap-2">
              <select
                name="countryCode"
                value={form.countryCode}
                onChange={handleChange}
                className="w-1/4 border border-gray-300 rounded-lg p-2 focus:outline-none"
              >
                <option value="+91">+91 (India)</option>
                <option value="+1">+1 (USA)</option>
                <option value="+44">+44 (UK)</option>
              </select>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handlePhoneChange}
                maxLength={10}
                className="w-3/4 border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="1234567890"
              />
            </div>
            {errors.phone && (
              <p className="text-red-600 text-sm mt-1">{errors.phone}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-700 font-medium">
              Company Name<span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="companyName"
              value={form.companyName}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Your Company"
            />
            {errors.companyName && (
              <p className="text-red-600 text-sm mt-1">{errors.companyName}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-700 font-medium">
              Purpose<span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="subject"
              value={form.subject}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Purpose of your message"
            />
            {errors.subject && (
              <p className="text-red-600 text-sm mt-1">{errors.subject}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-700 font-medium">
              Message<span className="text-red-600">*</span>
            </label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleChange}
              rows="2"
              className="w-full border border-gray-300 rounded-lg p-2 mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Write your message here..."
            />
            {errors.message && (
              <p className="text-red-600 text-sm mt-1">{errors.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={
              loading ||
              !form.firstName ||
              !form.lastName ||
              !form.email ||
              !form.phone ||
              !form.companyName ||
              !form.subject ||
              !form.message
            }
            className={`w-full font-semibold py-3 px-4 rounded-lg shadow-md transition duration-300 transform hover:scale-105 ${
              loading ||
              !form.firstName ||
              !form.lastName ||
              !form.email ||
              !form.phone ||
              !form.companyName ||
              !form.subject ||
              !form.message
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:from-blue-700 hover:to-purple-700 text-white"
            }`}
          >
            {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Contact;
